import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule} from '@angular/forms';
import {fieldsMatch} from '../../../_utils/validators';
import {UsersService} from '../../../_services/data/users.service';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, map, mergeMap, tap} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {NotificationService} from '../../../_services/gui/notification.service';
import {AuthenticationService} from '../../../_services/authentication.service';
import {DecodedJwtToken} from '../../../_data/admin/jwt-token';
import {isChangePasswordError} from '../../../_utils/type-guards';
import {HttpResponse} from '@angular/common/http';
import {ChangePasswordError} from '../../../_data/admin/change-password-error';
import {I18nService} from '../../../_services/gui/i18n.service';
import {exists} from '../../../_utils/operators';
import {NgTemplateOutlet, NgClass} from '@angular/common';
import {ValidationErrorsComponent} from '../../../common/forms/validation-errors/validation-errors.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [NgTemplateOutlet, ReactiveFormsModule, NgClass, ValidationErrorsComponent, TranslateModule]
})
export class ResetPasswordComponent implements OnInit {

  @Input() mode: 'token' | 'normal' = 'token';
  @Input() saveEnabled: boolean = true;
  @Output() passwordChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  token: string;
  formGroup: UntypedFormGroup;
  formSubmitted: boolean = false;

  private currentUser: DecodedJwtToken;
  private logoutOnChange = false;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);
  private usersService = inject(UsersService);
  private notificationService = inject(NotificationService);
  private i18nService = inject(I18nService);
  
  constructor() {
    const formBuilder = inject(UntypedFormBuilder);


    this.formGroup = formBuilder.group({
      currentPassword:    ['', this.normalMode()],
      newPassword:        ['', Validators.compose([Validators.required, ResetPasswordComponent.passwordStrength])],
      newPasswordConfirm: ['', Validators.compose([Validators.required, fieldsMatch('newPassword')])]
    });

    this.authenticationService.decodedToken$.subscribe(u => this.currentUser = u);

    // This ResetPassword component can be used as a standalone component (for resetting password by email, or password expired)
    // or as part of another component (eg: editing profile)
    //
    // Therefore, we need to check that when this component is the one the router sent us to (reset by email) we have a token parameter
    // The router will pass a flag as part of the data to tell us to check for this parameter
    this.route.data.pipe(
      //If there is route data
      exists(),
      //Filter if the tokenRequired flag is set to true by the router
      filter(value => !!value.tokenRequired),
      //Configure this component to know we need a token
      tap(() => this.mode = 'token'),
      //Grab the token param from the url
      map(() => this.route.snapshot.queryParamMap.get('token')),
      tap(o => console.debug(`Token: ${JSON.stringify(o)}`))
    ).subscribe(token => {
      //Ensure we have the token, and store it if we do.
      if(!!token) {
        this.token = token;
      } else {
        //If we don't have the token, forward the user to their home page
        console.info('Token was not provided as a parameter.  Navigating back to home');
        this.authenticationService.homePage$.pipe(mergeMap(homePage => this.router.navigateByUrl(homePage))).subscribe();
      }
    });

    //  If we're doing the password expired logic, we want to make sure the mode is set to normal.
    if(this.router.url === '/password-expired') {
      this.mode = 'normal';
      this.logoutOnChange = true;
    }
  }

  public static passwordStrength(control: AbstractControl): ValidationErrors | null {

    const pwd: string = control.value;

    if(!pwd) {
      return null;
    }
    
    const tooShort = pwd.length < 12;
    const tooLong = pwd.length > 30;
    const doesNotContainLcAlpha = !pwd.match('[a-z]');
    const doesNotContainUcAlpha = !pwd.match('[A-Z]');
    const doesNotContainNum = !pwd.match('[0-9]');
    const doesNotContainSpecial = !pwd.match(RegExp('[^a-z0-9]', 'i'));
    const doesNotContainSequentialAlpha= containsSequentialAlphaChar(pwd);
    const doesNotContainSequentialNumber = containsSequentialNumber(pwd);
    const doesNotContainsWhiteSpace = pwd.match(/\s/);
    const containsSequence = pwd.match(/(.)\1{2,}/);

    const invalid = tooShort || tooLong || doesNotContainLcAlpha || doesNotContainUcAlpha 
    || doesNotContainNum || doesNotContainSpecial || doesNotContainSequentialAlpha ||  doesNotContainSequentialNumber || doesNotContainsWhiteSpace || containsSequence;
    return invalid ? {tooShort, tooLong, doesNotContainLcAlpha, doesNotContainUcAlpha, 
      doesNotContainNum, doesNotContainSpecial, doesNotContainSequentialAlpha, doesNotContainSequentialNumber, doesNotContainsWhiteSpace, containsSequence} : null;
  }

  private normalMode(): ValidatorFn {
    return (c: AbstractControl) => this.mode === 'normal' && Validators.required(c);
  }

  /** Creates and returns a map function to convert the response to the response status and also sets form errors if need be */
  private checkPasswordChangeStatus() {
    return map((response: any) => {
      const responseObj = this.parseJson(response.body);
      if(response.status === 400 && isChangePasswordError(response.body)) {
        const obj = {
          tooShort: false, tooLong: false, doesNotContainLcAlpha: false, doesNotContainUcAlpha: false,
          doesNotContainNum: false, doesNotContainSpecial: false, containsSequence: false,
          doesNotContainsWhiteSpace: false
        };
        this.checkPasswordErrors(response.body.newPassword,obj);
        this.f.controls.newPassword.setErrors(obj);
      } else {
        this.handleResponseStatus(response, responseObj);
      }
      return response.status;
    });
  }

  private checkPasswordErrors(newPassword:string, obj:any) {
    if(newPassword.includes('TOO_SHORT')) {
      obj.tooShort = true;
    } 
    if(newPassword.includes('TOO_LONG')) {
      obj.tooLong = true;
    } 
    if(newPassword.includes('INSUFFICIENT_LOWERCASE')) {
      obj.doesNotContainLcAlpha = true;
    } 
    if(newPassword.includes('INSUFFICIENT_UPPERCASE')) {
      obj.doesNotContainUcAlpha = true;
    } 
    if(newPassword.includes('INSUFFICIENT_DIGIT')) {
      obj.doesNotContainNum = true;
    } 
    if(newPassword.includes('INSUFFICIENT_SPECIAL')) {
      obj.doesNotContainSpecial = true;
    } 
    if(newPassword.includes('ILLEGAL_ALPHABETICAL_SEQUENCE') ||
      newPassword.includes('ILLEGAL_NUMERICAL_SEQUENCE') ||
      newPassword.includes('ILLEGAL_MATCH')) {
      obj.containsSequence = true;
    } 
    if(newPassword.includes('ILLEGAL_WHITESPACE')) {
      obj.doesNotContainsWhiteSpace = true;
    }
  }
  
  private handleResponseStatus(response:any, responseObj:any) {
    if(response.status === 406) {
      this.f.setErrors({invalidToken: true});
    } else if(response.status === 412) {
      if(responseObj?.error === '602') {
        this.f.controls.newPassword.setErrors({recentlyUsedPassword: true});
      } else {
        this.f.controls.currentPassword.setErrors({incorrectPassword: true});
      }
    } else if(response.status === 409) {
      this.f.controls.newPassword.setErrors({recentlyUsedPassword: true});
    }
  }

  private parseJson(input: string) {
    let responseObj;
    try {
      responseObj = JSON.parse(input);
    } catch(e) {
      console.debug('Input string is not a JSON');
      return '';
    }
    return responseObj;
  }

  ngOnInit() {
    //We only set the title if we navigated directly to this component, which will be with a token
    if(this.mode === 'token') {
      this.i18nService.setTitle('title.admin.profile.reset-password');
    }
  }

  submit(value: any) {
    this.formSubmitted = true;
    if(this.formGroup.valid) {
      console.info('Submitting password reset...');
      let changePassword$: Observable<HttpResponse<string | ChangePasswordError>>;
      if(this.mode === 'token') {
        changePassword$ = this.usersService.resetPasswordByToken(value.newPassword, this.token);
      } else {
        changePassword$ = this.usersService.changePassword(this.currentUser.user.username, value.currentPassword, value.newPassword);
      }

      const changePasswordStatus$: Observable<number> = changePassword$.pipe(this.checkPasswordChangeStatus());

      console.debug('Subscribing to changePassword observable');
      changePasswordStatus$.subscribe(status => {
      if(status === 200) {
          this.notificationService.displayI18n('content.password-changed');
          this.formSubmitted = false;
          this.formGroup.reset();
          this.passwordChanged.emit(true);
          if(this.logoutOnChange) {
            this.authenticationService.logout();
          }
        }
      });
    }
  }

  public get f(): UntypedFormGroup {
    return this.formGroup;
  }

  public get c(): {[p: string]: AbstractControl} {
    return this.formGroup.controls;
  }

}
function containsSequentialAlphaChar(pwd:string):boolean {
  const alpha="abcdefghijklmnopqrstuvwxyz";
  const lowerpwd=pwd.toLowerCase();
  for(let i=0;i<lowerpwd.length-2;i++) {
    const sub=lowerpwd.substring(i,i+3);
    if(alpha.includes(sub)) {
      return true;
    }
  }
  return false;
}

function containsSequentialNumber(pwd:string):boolean {
  const numb="0123456789";
  for(let i=0;i<pwd.length-2;i++) {
    const sub=pwd.substring(i,i+3);
    if(numb.includes(sub)) {
      return true;
    }
  }
  return false;
}

